import { Column, Typography } from '@/components'
import {
  FormAutoCompleteItem,
  findBusinessType,
  OptionType,
} from '@/components/molecules/FormItem/FormAutocompleteItem'
import { AssessmentStepKeys, Breakpoint, ThemeColor } from '@/enums'
import { useSegment } from '@/modules'
import {
  useAssessmentValues,
  useSaveAssessmentStep,
} from '@/pages/SelfAssessmentPage/helpers'
import { useFormContext, useOnBackClick, useOnNextClick } from '@/providers'
import { useLocationTypes } from '@/services/locationTypes'
import { themeColor, useIsMobile } from '@/utils'
import { Alert, Button } from '@surein/ui'
import { Plus, TriangleAlert } from 'lucide-react'
import { isNil } from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import { WhyDoWeAsk } from './components/WhyDoWeAsk/WhyDoWeAsk'
import { useBusinessTypeOptions } from './hooks/useBusinessTypeOptions'
import { useIgnoreAssessmentStep } from './hooks/useIgnoreAssessmentStep'

interface SecondarySelection {
  id: string
  value?: string
  label?: string
}

interface BusinessTypeStepConfirmationAIProps {
  onBack: () => void
  categoryId?: string
}

interface FormValues {
  locationSubtypes?: string[]
  locationType?: string
  businessType?: string
  locationTypeSpecification?: string
}

interface AssessmentValues {
  businessTypeAi?: {
    locationSubtypes?: string[]
    locationSubtypesPromptFailed?: boolean
  }
  businessType?: {
    businessType: string
    locationType: string
    locationSubtypes: string[]
    locationTypeSpecification: string
  }
}

const SecondaryActivityWrapper = styled(Column)`
  gap: 24px;
`

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Subtitle = styled(Typography).attrs({
  variant: 'description',
})`
  display: block;
  line-height: 2;
  color: ${themeColor(ThemeColor.glass500)};
  margin-top: -24px;
`

const HIDDEN_OPTIONS = ['online']

const searchSortFn = (
  a: OptionType & { ratio?: number },
  b: OptionType & { ratio?: number },
) => {
  if (!isNil(a.ratio) && !isNil(b.ratio)) {
    return b.ratio - a.ratio
  }
  return 0
}

export const BusinessTypeStepConfirmationAI = ({
  onBack,
  categoryId,
}: BusinessTypeStepConfirmationAIProps) => {
  const { t } = useTranslation()
  const { isMobile } = useIsMobile(Breakpoint.sm)
  const { setFieldValue, values } = useFormContext<FormValues>()
  const [secondarySelections, setSecondarySelections] = useState<
    SecondarySelection[]
  >([])
  const { save } = useSaveAssessmentStep(AssessmentStepKeys.businessType)
  const { options } = useBusinessTypeOptions()
  const {
    values: allAssessmentValues,
    dispatchValues,
  } = useAssessmentValues() as {
    values: AssessmentValues
    dispatchValues: (values: AssessmentValues) => void
  }
  const {
    data: locationTypesData,
    isLoading: locationTypesLoading,
  } = useLocationTypes()
  const promptedAiSubtypes =
    allAssessmentValues?.businessTypeAi?.locationSubtypes
  const isPromptFailed =
    allAssessmentValues?.businessTypeAi?.locationSubtypesPromptFailed === true

  const analytics = useSegment()
  const { addIgnoredStep, removeIgnoredStep } = useIgnoreAssessmentStep({
    categoryId: categoryId ?? '',
  })

  useEffect(() => {
    if (!values.locationSubtypes?.length && promptedAiSubtypes?.length) {
      setFieldValue('locationSubtypes', promptedAiSubtypes)
      dispatchValues({ ['businessTypeAi']: { locationSubtypes: [] } })
    } else if (values.locationSubtypes?.length) {
      setSecondarySelections(
        values.locationSubtypes.map((subtype) => ({
          id: uuidv4(),
          value: subtype,
        })),
      )
    } else if (isPromptFailed) {
      setSecondarySelections([{ id: uuidv4(), value: undefined }])
    }
  }, [])

  useEffect(() => {
    if (values.locationSubtypes?.length) {
      setSecondarySelections(
        values.locationSubtypes.map((subtype) => ({
          id: uuidv4(),
          value: subtype,
        })),
      )
      dispatchValues({ ['businessTypeAi']: { locationSubtypes: [] } })
    }
  }, [values.locationSubtypes])

  useEffect(() => {
    // Check if we have only one location type selected
    // if so, we skip the main activity selection step
    if (values.locationSubtypes?.length === 1 && categoryId) {
      addIgnoredStep(AssessmentStepKeys.businessTypeMainSelectionAi)
    } else if (
      categoryId &&
      values.locationSubtypes &&
      values.locationSubtypes?.length > 1
    ) {
      removeIgnoredStep(AssessmentStepKeys.businessTypeMainSelectionAi)
    }
  }, [values.locationSubtypes, categoryId, addIgnoredStep, removeIgnoredStep])

  const handleAddSecondary = () => {
    const allFilled = secondarySelections.every(
      (selection) => !!selection.value,
    )

    if (allFilled && secondarySelections.length < 4) {
      const newId = uuidv4()
      setSecondarySelections([
        ...secondarySelections,
        { id: newId, value: undefined },
      ])
    }
  }

  const handleSelectSecondary = useCallback(
    async (option: OptionType | null, id: string) => {
      const newSelections = secondarySelections.map((item) =>
        item.id === id
          ? { ...item, value: option?.value, label: option?.label }
          : item,
      )

      setSecondarySelections(newSelections)

      const locationSubtypes = newSelections.map((item) => item.value)

      setFieldValue('locationSubtypes', locationSubtypes)

      await analytics.track('business_type_add_activity', {
        activity: option?.value,
      })
    },
    [secondarySelections, setFieldValue],
  )

  const handleClearSecondary = useCallback(
    async (id: string) => {
      const secondarySelectionsLength = secondarySelections.length
      const activityBeingDeleted = secondarySelections.find(
        (item) => item.id === id,
      )?.value
      const filtered = secondarySelections.filter((item) => item.id !== id)
      const locationSubtypes = filtered.map((item) => item.value)
      const newSecondarySelections =
        secondarySelectionsLength === 1
          ? [{ id: uuidv4(), value: undefined }]
          : filtered

      setSecondarySelections(newSecondarySelections)
      setFieldValue('locationSubtypes', locationSubtypes)
      await analytics.track('business_type_add_activity_delete', {
        activity: activityBeingDeleted,
      })
    },
    [secondarySelections, setFieldValue],
  )

  const saveAndTrack = useCallback(async () => {
    await save()

    if (values.locationSubtypes?.length === 1) {
      const optionData = options?.find(
        (option) => option.value === values.locationSubtypes?.[0],
      )

      if (optionData) {
        const businessType = findBusinessType(
          optionData,
          locationTypesData?.verticalsMap ?? {},
          locationTypesData?.getVerticalLabel,
        ).value

        dispatchValues({
          ['businessType']: {
            businessType,
            locationType: values.locationSubtypes[0],
            locationSubtypes: [],
            locationTypeSpecification: '',
          },
        })
      }
    }

    await analytics.track('business_type_add_activity_success')
  }, [values, options])

  const goBack = useCallback(async () => {
    await setFieldValue('locationSubtypes', [])
    onBack()
  }, [setFieldValue])

  useOnNextClick(saveAndTrack)

  useOnBackClick(goBack)

  const shouldShowAddOthersButton =
    !(values.businessType === 'other' && values?.locationTypeSpecification) &&
    secondarySelections.length < 4 &&
    values.locationSubtypes?.length !== 0

  const subtitle = isPromptFailed
    ? t(
        'selfAssessment.locationType.businessTypeConfirmationAiPromptFailedSubtitle',
      )
    : t(
        'selfAssessment.locationType.businessTypeConfirmationAiPromptSucceedSubtitle',
      )

  return (
    <StepWrapper>
      <Subtitle>{subtitle}</Subtitle>
      {isPromptFailed && (
        <Alert
          icon={TriangleAlert}
          text={t(
            'selfAssessment.locationType.businessTypeConfirmationAiPromptFailedAlert',
          )}
          variant="warning"
        />
      )}
      {
        <SecondaryActivityWrapper>
          {secondarySelections.map((item, _, arr) => (
            <FormAutoCompleteItem
              key={item.id}
              initialValue={
                values.locationSubtypes?.includes(item.value ?? '')
                  ? ({
                      value: item.value ?? '',
                      label:
                        options.find((option) => option.value === item.value)
                          ?.label ?? item.value,
                    } as OptionType)
                  : null
              }
              name={`${
                !item.value && arr.length === 1
                  ? 'locationSubtypes'
                  : `secondary-${item.id}`
              }`}
              omitOptions={[
                values.locationType ?? '',
                ...HIDDEN_OPTIONS,
                ...secondarySelections.map((i) => i.value ?? ''),
              ]}
              onBlur={() => handleClearSecondary(item.id)}
              onClear={() => handleClearSecondary(item.id)}
              onValueChange={(option) => handleSelectSecondary(option, item.id)}
              options={options}
              shouldFocusOnRender
              shouldRenderInModal={isMobile}
              sortFn={searchSortFn}
              isLoading={locationTypesLoading}
              layout="vertical"
            />
          ))}
        </SecondaryActivityWrapper>
      }

      <div>
        {shouldShowAddOthersButton && (
          <Button
            onClick={handleAddSecondary}
            size="sm"
            suffixIcon={Plus}
            variant="muted"
            type="button"
          >
            {t('addOtherActivities')}
          </Button>
        )}
      </div>

      <WhyDoWeAsk />
    </StepWrapper>
  )
}
